import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@material-ui/core';
import {
  ExpandLess,
  ExpandMore,
  LockOutlined
} from '@material-ui/icons';

import useModulePermissions from '../../../../../../hooks/useModulePermissions';
import { menuByModule } from '../../../../../../routeConfig';

const useStyles = makeStyles((theme) => ({
  menu: {
    paddingLeft: theme.spacing(1)
  },
  submenu: {
    paddingLeft: theme.spacing(3)
  },
  subSubmenu: {
    paddingLeft: theme.spacing(5)
  },
  icon: {
    minWidth: 32,
    color: '#546e7a'
  },
  subtleTitle: {
    fontWeight: 500,
    color: '#3c4345'
  },
  submenuFade: {
    animation: `$fadeInSlide 400ms ease-out`,
    position: 'relative',
    zIndex: 5,
    backgroundColor: '#fff',
    borderRadius: 6,
    boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
    padding: '4px 0',
    animationFillMode: 'forwards',
    transition: 'box-shadow 400ms ease, background-color 400ms ease'
  },
  '@keyframes fadeInSlide': {
    from: {
      opacity: 0,
      transform: 'translateY(-8px)',
      boxShadow: '0 0 0 rgba(0, 0, 0, 0)',
      backgroundColor: 'transparent'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      backgroundColor: '#fff'
    }
  },
  disabledItem: {
    opacity: 0.55,
    cursor: 'not-allowed',
    userSelect: 'none',
    '& *': {
      cursor: 'not-allowed'
    }
  },
  lockedIcon: {
    color: theme.palette.text.disabled
  },
  emptyMessage: {
    paddingLeft: theme.spacing(4),
    opacity: 0.6,
    fontStyle: 'italic'
  }
}));

/**
 * Mantém todos os itens visíveis e desabilita somente as folhas
 * para as quais o usuário não possui permissão.
 */
function buildMenuTree(items, hasPerm) {
  return (items || []).map((item) => {
    const hasChildren = Boolean(
      item.children && item.children.length > 0
    );

    if (!hasChildren) {
      const allowed =
        !item.permCode ||
        Number(item.permCode) === 0 ||
        hasPerm(item.permCode);

      return {
        ...item,
        visible: item.visible !== false,
        disabled: !allowed,
        children: []
      };
    }

    return {
      ...item,
      visible: item.visible !== false,
      disabled: false,
      children: buildMenuTree(item.children, hasPerm)
    };
  });
}

function getItemLabel(item) {
  return (
    item.fallbackLabel ||
    item.label ||
    item.title ||
    item.name ||
    ''
  );
}

function getPathname(to) {
  if (typeof to === 'string') {
    return to;
  }

  if (to && typeof to === 'object') {
    return to.pathname || '';
  }

  return '';
}

function isCurrentRoute(locationPathname, itemTo) {
  const itemPathname = getPathname(itemTo);

  if (!itemPathname) {
    return false;
  }

  return locationPathname === itemPathname;
}

const SidebarNav = ({ className, moduleCode }) => {
  const classes = useStyles();
  const location = useLocation();
  const { hasPerm, loading } = useModulePermissions(moduleCode);
  const [openMap, setOpenMap] = useState({});

  const rawMenu = useMemo(() => {
    return menuByModule[moduleCode] || [];
  }, [moduleCode]);

  const finalMenu = useMemo(() => {
    return buildMenuTree(rawMenu, hasPerm);
  }, [rawMenu, hasPerm]);

  useEffect(() => {
    setOpenMap({});
  }, [location.pathname]);

  const isOpen = (key) => Boolean(openMap[key]);

  const findAncestorKeys = (items, targetKey, ancestors = []) => {
    for (const currentItem of items) {
      if (currentItem.key === targetKey) {
        return ancestors;
      }

      if (currentItem.children && currentItem.children.length > 0) {
        const result = findAncestorKeys(
          currentItem.children,
          targetKey,
          [...ancestors, currentItem.key]
        );

        if (result) {
          return result;
        }
      }
    }

    return null;
  };

  const getDescendantKeys = (item) => {
    if (!item.children || item.children.length === 0) {
      return [];
    }

    return item.children.reduce((keys, child) => {
      return [
        ...keys,
        child.key,
        ...getDescendantKeys(child)
      ];
    }, []);
  };

  const toggleOpen = (item) => {
    setOpenMap((previous) => {
      const currentlyOpen = Boolean(previous[item.key]);

      if (currentlyOpen) {
        const next = {
          ...previous,
          [item.key]: false
        };

        getDescendantKeys(item).forEach((childKey) => {
          next[childKey] = false;
        });

        return next;
      }

      const ancestorKeys =
        findAncestorKeys(finalMenu, item.key) || [];

      const next = {};

      ancestorKeys.forEach((ancestorKey) => {
        next[ancestorKey] = true;
      });

      next[item.key] = true;

      return next;
    });
  };

  const renderItems = (items, level = 0) => {
    return items.map((item) => {
      if (!item.visible) {
        return null;
      }

      const Icon = item.icon;
      const hasChildren = Boolean(
        item.children && item.children.length > 0
      );
      const itemLabel = getItemLabel(item);

      const levelClass =
        level === 0
          ? classes.menu
          : level === 1
            ? classes.submenu
            : classes.subSubmenu;

      if (!hasChildren) {
        const commonProps = {
          key: item.key,
          className: clsx(
            levelClass,
            item.disabled && classes.disabledItem
          )
        };

        if (item.disabled || !item.to) {
          const blockInteraction = (event) => {
            event.preventDefault();
            event.stopPropagation();
          };

          return (
            <Tooltip
              key={item.key}
              title="Usuário sem permissão para acessar esta opção"
              placement="right"
              arrow
            >
              <ListItem
                {...commonProps}
                button={false}
                component="div"
                role="menuitem"
                aria-disabled="true"
                tabIndex={-1}
                onClick={blockInteraction}
                onMouseDown={blockInteraction}
                onKeyDown={blockInteraction}
              >
                <ListItemIcon
                  className={clsx(
                    classes.icon,
                    classes.lockedIcon
                  )}
                >
                  <LockOutlined fontSize="small" />
                </ListItemIcon>

                <ListItemText
                  primary={itemLabel}
                  primaryTypographyProps={{
                    className: classes.subtleTitle
                  }}
                />
              </ListItem>
            </Tooltip>
          );
        }

        return (
          <ListItem
            {...commonProps}
            button
            component={Link}
            to={item.to}
            selected={isCurrentRoute(location.pathname, item.to)}
          >
            <ListItemIcon className={classes.icon}>
              {Icon ? <Icon /> : null}
            </ListItemIcon>

            <ListItemText
              primary={itemLabel}
              primaryTypographyProps={{
                className: classes.subtleTitle
              }}
            />
          </ListItem>
        );
      }

      const enabledChildren = item.children.filter(
        (child) => !child.disabled
      );

      return (
        <div key={item.key}>
          <ListItem
            button
            className={levelClass}
            onClick={() => toggleOpen(item)}
          >
            <ListItemIcon className={classes.icon}>
              {Icon ? <Icon /> : null}
            </ListItemIcon>

            <ListItemText
              primary={itemLabel}
              primaryTypographyProps={{
                className: classes.subtleTitle
              }}
            />

            {isOpen(item.key) ? <ExpandLess /> : <ExpandMore />}
          </ListItem>

          <Collapse
            in={isOpen(item.key)}
            timeout={400}
            unmountOnExit
          >
            <List
              component="div"
              disablePadding
              className={classes.submenuFade}
            >
              {enabledChildren.length > 0 ? (
                renderItems(item.children, level + 1)
              ) : (
                <ListItem className={levelClass}>
                  <ListItemText
                    primary="Nenhuma opção disponível"
                    primaryTypographyProps={{
                      className: classes.emptyMessage
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Collapse>
        </div>
      );
    });
  };

  return (
    <List className={className}>
      {loading ? (
        <ListItem>
          <ListItemText primary="Carregando permissões..." />
        </ListItem>
      ) : (
        renderItems(finalMenu)
      )}
    </List>
  );
};

SidebarNav.propTypes = {
  className: PropTypes.string,
  moduleCode: PropTypes.number.isRequired
};

export default SidebarNav;
